/**
 * The Chronicle — the durable record of what a god actually did, and of what
 * the world durably became.
 *
 * `ROADMAP.md` §17.5 item 5 and §18 both put this ahead of everything else in
 * progression, on one argument: `D §6.2`'s outer Web asks *have you actually
 * lived this?* and `EventBus` is a 2,000-record ring. `tools/chronicle.ts`
 * turned that argument into a number — **the ring spans 0.470 world-years,
 * and 99.6% of it is herd movement, so it holds about THREE durable events at
 * a time.** A century qualification cannot read that, and no amount of tuning
 * the ring changes it: the ring is sized for perception, which is what it is
 * good at, and perception is not memory.
 *
 * ## This is not a bigger ring
 *
 * A log that kept every record would grow with the world's firehose (4,258
 * events a world-year, measured) and with the god's economy (56k–234k acts in
 * a shipped-clock Age). Law 2 says the world is BASELINE + SPARSE HISTORY, and
 * that applies to a god's own history exactly as it applies to the ground:
 * **untouched ground costs nothing; only divergence is stored.** So:
 *
 *  · Deeds fold into STRANDS — one per (god, verb, subject) — where a subject
 *    is the thing the act named, or the 260 m place it landed on when it named
 *    nothing. Four hundred visits to one hillside are one relationship with
 *    one place, not four hundred rows.
 *  · A strand that never crossed into persistence is RETIRED. This is the half
 *    the fold alone could not carry: measured, a roaming god's 56k acts fold to
 *    47k strands at 260 m, which is a fold ratio of 1.2 and no help at all.
 *    What bounds the journal is not folding but forgetting — and forgetting the
 *    right thing, which is everything that left nothing behind.
 *  · The world's own durable history folds by PLACE, so it is bounded by the
 *    island rather than by the calendar. 5.7 durable events a world-year is
 *    cheap; what makes it permanently cheap is that a finite world has a finite
 *    number of places for them to happen in.
 *
 * ## What counts as having endured
 *
 * Not repetition. Law 25 is explicit that progression reads consequence and
 * **never repetition** — "the same act on the same subject in the same
 * conditions must approach zero" — so casting on one tree a thousand times
 * earns a strand nothing by itself. What earns it is the *world* saying
 * something changed: a thing promoted, a stone bound, earth moved, an aura
 * lit, or a causal child through `parentEventIds`, which is the one place the
 * simulation records what actually caused what.
 *
 * That is `D §6`'s ACT → CONSEQUENCE → PERSISTENCE made mechanical, and it has
 * a pleasant consequence of its own: a god who blesses bare ground forever
 * accumulates nothing, because `EnvField` sweeps the blessing away in about
 * eighteen days and there is nothing left to have lived through. A god who
 * tends one tree until it stops being ordinary keeps that strand for good.
 *
 * ## The wall
 *
 * The Chronicle records `godId`. That is world bookkeeping of exactly the kind
 * `events.ts` describes — the same class as `EventCause`'s own `godId` — and it
 * must never reach a mortal, a band's memory, or a rival god. Nothing under
 * `src/sim/mortals` reads this file, and `test/deity.ts` keeps it that way.
 *
 * ## Two things are called the Chronicle, and this is the other one
 *
 * `ui/hud.ts` has a panel called the chronicle — the scrolling feed of what
 * the player can perceive nearby. That one takes `PerceivedEvent`s, is a
 * display, and forgets everything the instant it scrolls. This one is the
 * world's own record, takes `EventRecord`s, and is what `DESIGN-III.md`'s law
 * 16 means by "a Chronicle event": *any change the world can remember*. The
 * feed is named after the record, not the other way round. Nothing imports
 * across the two — there is no name collision in code, only in prose.
 *
 * Pure by law 7: no three, no `Math.random`, no wall clock. Every horizon here
 * is in WORLD-seconds, so the journal's bounds do not move when the clock's
 * speed does — which matters, because `D §4` multiplies that speed by 52.
 */

import type { EventCause, EventRecord, EventType } from './events';
import { EARTH_PER_POWER } from '@/world/earth';

/* ── resolution and horizons ───────────────────────────────────────────── */

/**
 * The place a deed is remembered at, in metres. 260 m is the region's own cell
 * — the world's existing answer to "how big is a place" — so a strand's ground
 * is the same ground `region.siteAt` calls one site.
 */
export const CHRONICLE_CELL = 260;

/**
 * And the coarser grid the world's own durable history folds onto. One
 * kilometre: a burn, a road or an abandoned camp is a *neighbourhood* event,
 * and folding them finer would store the same history several times.
 */
export const MARK_CELL = 1040;

/**
 * How long a strand that has come to nothing is kept before it is forgotten,
 * in world-seconds. One world-year is deliberately generous — `EnvField`
 * sweeps a blessing at about eighteen days, so anything still standing at a
 * year has outlived its own conditions layer twenty times over.
 *
 * The bound this buys is worth stating: at the measured ceiling of ~620 casts
 * a world-year, no more than ~620 barren strands can be alive at once, at any
 * clock speed, for ever.
 */
export const FORGET_AFTER = 365 * 86400;

/**
 * How much earth has to move before the world remembers it, in cubic metres.
 *
 * It was `POWER_PER_DAY × EARTH_PER_POWER` — a day of the god's own
 * regeneration at 0.02, and therefore, exactly, a full scoop by the hand of a
 * god that has only a day left: 12.6 m across and 4.2 m deep, 1,000 m³. On
 * 2026-08-18 regeneration became a five-stone reservoir refilling at 0.35 a
 * day (the cast economy; `anchor.ts`), and a day of THAT is not the measure
 * of a memorable scoop — the ground remembers a volume, not a rate. So the
 * measured thousand is pinned here, written as the product it was derived
 * from, so the derivation is not lost: `0.02 × EARTH_PER_POWER`.
 */
export const EARTH_MARK_VOLUME = 0.02 * EARTH_PER_POWER;

/**
 * Durable event types — the ones whose change outlives its own season.
 *
 * This is not an editorial list. Every one of these lands in a sparse history
 * store that measures its memory in decades (`things`, `sere`, `earth`,
 * `anchors`) or in the mortal record itself, rather than in a field that
 * sweeps itself clean in weeks. `fire_started` is deliberately absent and
 * `fire_burned` deliberately present: an ignition is an event, a burn is a
 * sere. Both are emitted now (`ROADMAP.md` §28) — 3.5 ignitions a world-year
 * of which 0.67 burn enough ground to be worth remembering — so the ratio
 * this set was written to express is real rather than aspirational: most
 * fires are weather, and a few are history.
 */
export const DURABLE: ReadonlySet<EventType> = new Set<EventType>([
  'fire_burned', 'thing_promoted', 'anchor_placed', 'sacred_raised',
  'structure_completed', 'path_formed', 'camp_abandoned',
  // the inverse of camp_abandoned (2026-08-23): somebody moved into what
  // the dead left standing. Need-driven and at most one a day per band, so
  // it is rarer than completion by construction. Priced on the third
  // century (`tools/fireside.ts --grow 12`, god-free): see ROADMAP §49.10.
  'structure_claimed',
  'band_split', 'band_merged', 'culture_diverged',
  'divine_earth', 'divine_grasp', 'divine_aura',
  // natural history is real history (law 16): a species gone from a cell and
  // one come back after an absence — ROADMAP §42's two crossings. Priced
  // before admission (`tools/durable.ts`, one god-free year on the shipped
  // world): 24 new marks a year without them, ~25 with — 0 vanished, 1
  // returned; vanishings come from big burns and Weaken.
  'wildlife_vanished', 'wildlife_returned',
  // somebody went to look where a sign pointed, and found out (2026-08-22,
  // horizon C for Omen). The trip is the band's own doing and the mark is
  // the world's record that people came here following something they could
  // not explain — whatever they found. Priced before admission
  // (`tools/durable.ts`): a god-free year emits none, so every one of these
  // marks is downstream of a cast, and they fold by kilometre like the rest.
  'omen_answered',
]);

/**
 * Durable changes that are *the world's own doing at the level of a system* —
 * settlements appearing and emptying, peoples dividing, roads forming.
 *
 * `wildlife_migrated` is pointedly NOT here, and that is the whole reason the
 * set exists: it is 99.6% of everything the world emits (12,728 of 12,774 in
 * three world-years), so any rule that admits it is measuring the firehose and
 * calling it history. `tools/eventrate.ts` found the same trap from the other
 * end and the same answer applies — the herd log is perception, not memory.
 */
export const SYSTEM_CHANGE: ReadonlySet<EventType> = new Set<EventType>([
  'structure_completed', 'path_formed', 'camp_abandoned',
  'band_split', 'band_merged', 'culture_diverged',
]);

/**
 * Which verb a divine event was. Undefined for anything that is not a cast.
 *
 * Exported so `tools/level.ts` can enumerate the (verb, endurance) pairs the
 * build is capable of producing rather than keeping its own copy of the list —
 * the same discipline `tools/wildlife.ts` uses, and for the same reason: a
 * hand-copied table in a probe goes stale silently and then the probe lies.
 */
export const VERB_OF: Partial<Record<EventType, string>> = {
  divine_strike: 'strike', divine_rain: 'rain',
  divine_strengthen: 'strengthen', divine_weaken: 'weaken',
  divine_omen: 'omen', divine_grasp: 'grasp', divine_earth: 'earth',
  divine_aura: 'aura', anchor_placed: 'anchor',
};

/**
 * How a strand came to have endured — the world's word, never the god's.
 *
 * `answer` and `becoming` (2026-08-22) are horizon C for the two verbs that
 * had none: an Omen that somebody went to look at, and a press on a life
 * that carried an axis over a line the card speaks at. Both arrive only as
 * causal children (`parentEventIds`) of the cast, so repetition on the same
 * subject is one strand however often it is done (law 25), and a becoming
 * ends when the person does (`lapsed`).
 */
export type Endurance =
  'promotion' | 'anchor' | 'earth' | 'aura' | 'grasp' | 'fire' | 'burn'
  | 'answer' | 'becoming';

/* ── the two registers ─────────────────────────────────────────────────── */

/**
 * A god's relationship with one subject: every act of one verb on one thing,
 * or on one 260 m piece of ground, folded into a single row.
 *
 * `travel` is the load-bearing oddity. A subject that is a *thing* can move —
 * a followed person walks, a herd migrates — so the distance between where a
 * strand began and the furthest its acts have since landed says whether the
 * god kept hold of something that was going somewhere. That is a fact about
 * consequence, not about attention: it is measured from acts, and looking at
 * a subject contributes nothing to it (laws 9 and 15).
 */
export interface Strand {
  /** `${godId}|${verb}|${subject}` — stable, and what the allocation stores. */
  key: string;
  godId: string;
  verb: string;
  /** The thing the acts named, or `cell:gx,gz` where they named nothing. */
  subject: string;
  /** Where the first act landed. */
  x: number;
  z: number;
  /** World-seconds of the first and most recent act. */
  first: number;
  last: number;
  acts: number;
  /** Furthest any later act landed from where the strand began, in metres. */
  travel: number;
  /** When the world first said this had endured. Absent means it has not. */
  enduredAt?: number;
  /**
   * When the world WITHDREW its confirmation. Absent means it still stands.
   *
   * A promoted thing is not promoted for ever: `ThingStore` decays a mark on a
   * 25-year half-life, and below `PROMOTE` the tree is an ordinary tree again.
   * Nothing used to notice. `enduredAt` was set once and never unset, and
   * `standingOf`'s persistence term GROWS with age — so a god went on being
   * paid, at an increasing rate, for a promotion the world had forgotten.
   * That is the exact inverse of law 25: progression is supposed to read
   * consequence, and this read the absence of one.
   *
   * It rides the snapshot for free — `toSnapshot` and `restore` both spread —
   * and `JSON.stringify` drops `undefined`, so an old save loads with every
   * strand still standing, which is the conservative direction.
   */
  endedAt?: number;
  /** How, in the order the world said so. Empty until something does. */
  left: Endurance[];
}

/**
 * One place where the world durably changed, folded by kind and kilometre.
 * Bounded by the island rather than by the calendar, which is the property
 * that makes keeping it for ever affordable.
 */
export interface Mark {
  /** `${type}|${gx},${gz}` at MARK_CELL. */
  key: string;
  type: EventType;
  x: number;
  z: number;
  first: number;
  last: number;
  count: number;
  /** True if any of the folded events was a god's doing. */
  divine: boolean;
  /** Whose, if a god's and if the world recorded it. Never leaves the sim. */
  by?: string;
}

/** What a god's whole record adds up to, without walking its strands. */
export interface Tally {
  /** Acts of any verb, retired ones included. */
  acts: number;
  /** Strands forgotten for having come to nothing, and the acts inside them. */
  retired: number;
  retiredActs: number;
  /** World-seconds of the first and most recent act of any kind. */
  first?: number;
  last?: number;
}

export interface ChronicleSnapshot {
  strands: Strand[];
  marks: Mark[];
  tallies: Array<Tally & { godId: string }>;
  /** Recent divine event ids, so a causal child can still find its parent. */
  pending: Array<{ id: string; key: string }>;
}

/* ── helpers ───────────────────────────────────────────────────────────── */

const cellOf = (v: number, size: number): number => Math.floor(v / size);

const placeKey = (x: number, z: number, size: number): string =>
  `${cellOf(x, size)},${cellOf(z, size)}`;

/**
 * How many causal parents to keep findable. A child event follows its parent
 * within the same tick or the next few (`fire_started` is emitted inside the
 * same `act` call), so this only has to survive a burst — and it is bounded
 * rather than growing, because it is a lookup aid and not a record.
 */
const PENDING_MAX = 256;

export class Chronicle {
  private readonly strandMap = new Map<string, Strand>();
  private readonly markMap = new Map<string, Mark>();
  private readonly tallyMap = new Map<string, Tally>();
  /** divine event id → strand key, newest last. Bounded by PENDING_MAX. */
  private readonly pending = new Map<string, string>();

  /* ── writing ─────────────────────────────────────────────────────────── */

  /**
   * Fold one event in. Called from the same `onEmit` the witness system runs
   * on, so the journal is written at emission time — which is simulation time,
   * in emission order — and a deterministic run writes a deterministic
   * journal.
   *
   * Three things happen here and they are independent: a divine act extends
   * its strand; a durable change marks its place; and a durable change
   * standing on a strand's ground, or descending from one of its events, tells
   * that strand it endured.
   */
  record(e: EventRecord): void {
    const divine = e.cause.kind === 'divine';
    const godId = e.cause.kind === 'divine' ? e.cause.godId : undefined;

    // 0. a becoming ends with the life it was in. The same rule as a lapsed
    //    promotion (A13): a strand whose confirmation the world has withdrawn
    //    stops accruing standing, frozen rather than erased (law 33).
    if (e.type === 'person_died' && typeof e.data?.id === 'string') {
      this.lapsed(e.data.id, e.time, 'becoming');
    }

    // 1. the deed register — the god's own hand, folded by subject
    let key: string | undefined;
    const verb = VERB_OF[e.type];
    if (divine && godId && verb) {
      key = this.extend(godId, verb, e);
      this.remember(e.id, key);
    }

    // 2. the world's durable history, folded by place
    if (DURABLE.has(e.type) && this.worthRemembering(e)) this.mark(e, divine, godId);

    // 3. what endured. A durable event is the world saying something changed,
    //    and law 25 refuses to let repetition say it instead.
    const how = this.worthRemembering(e) ? this.enduranceOf(e.type) : undefined;
    if (how) {
      // the act's own strand, when the durable event IS the act
      if (key) this.endured(key, e.time, how);
      // A causal child — the one place the simulation records true cause. The
      // child then INHERITS the strand, so the chain carries to any depth:
      // strike → `fire_started` → `fire_burned` is two hops, and without the
      // inheritance it broke at the second. `fire_started` is natural, so it
      // is never remembered on its own account, and a burn's parent is the
      // ignition rather than the hand that made it — which is exactly right
      // for law 10 and exactly why the link has to be walked rather than
      // flattened into the event.
      for (const parent of e.parentEventIds ?? []) {
        const owner = this.pending.get(parent);
        if (owner) { this.endured(owner, e.time, how); this.remember(e.id, owner); }
      }
      // and whatever of this god's the change actually stands on. When the
      // durable event names a subject — `thing_promoted` carries the very
      // `targetId` the casts that promoted it carried — only that subject's
      // strands endure. Falling back to the whole cell would credit a Weaken
      // on the same hillside for a tree a Strengthen made important, which is
      // not consequence, it is proximity.
      //
      // The cell fallback is for the durable events that name no subject
      // because their subject IS the ground: an anchor, an earthwork, an aura.
      // Those endure every barren strand of yours in that 260 m cell, and they
      // should — a god that worked a hillside and then came down and bound it
      // did make that ground into something, and an Anchor costs 60% of a
      // whole god to say so.
      if (godId) {
        const named = typeof e.data?.targetId === 'string' ? e.data.targetId : undefined;
        const at = placeKey(e.x, e.z, CHRONICLE_CELL);
        for (const s of this.strandMap.values()) {
          if (s.godId !== godId || s.enduredAt !== undefined || s.first > e.time) continue;
          const mine = named !== undefined
            ? s.subject === named
            : placeKey(s.x, s.z, CHRONICLE_CELL) === at;
          if (mine) this.endured(s.key, e.time, how);
        }
      }
    }
  }

  /**
   * Is this event large enough to be history at all?
   *
   * The type is not always the whole answer. Fire learned this first: 81% of
   * ignitions burn two cells or fewer, so `fire_burned` is emitted only past
   * `BURN_MARK_CELLS` and a small fire is weather rather than history. Earth
   * needs the same judgement for the opposite reason — fire's size is the
   * world's choice and a scoop's size is the player's, so nothing upstream can
   * filter it, and the moment `moveEarth` got a key (§29) the cheapest road to
   * a kind's whole standing became twenty dimples spread 260 m apart.
   *
   * Measured in `tools/earthmark.ts`: identical acts at identical spend earn
   * level 7 in one Chronicle cell and level 14 across twenty, and a 12 m scoop
   * reaches that ceiling for 0.35 of a god where twenty Anchors — the other
   * faculty that endures the instant it acts — cost 12.
   *
   * The line is `EARTH_MARK_VOLUME`, and it is stated in the world's own
   * economy rather than picked: **a day of the god's own regeneration.** It is
   * not below visibility and does not pretend to be — a full hold at 12 m is a
   * real 24 m pit and falls under it — the claim is that a longhouse-sized
   * scoop is not an event in the history of a 36 km island. The act still
   * happens, still costs and still emits, so mortals still feel that something
   * happened here (law 10 is untouched); the world simply does not write it
   * down.
   */
  private worthRemembering(e: EventRecord): boolean {
    if (e.type !== 'divine_earth') return true;
    const v = e.data?.volume;
    // A `divine_earth` with no volume in it is `shapeLand`'s, which only ever
    // hurries a process the land was already running: those are read off the
    // ground rather than chosen, so they are not the thing this filters.
    return typeof v !== 'number' || v >= EARTH_MARK_VOLUME;
  }

  /** Which kind of persistence an event type is evidence of, if any. */
  private enduranceOf(t: EventType): Endurance | undefined {
    switch (t) {
      case 'thing_promoted': return 'promotion';
      case 'anchor_placed': return 'anchor';
      case 'divine_earth': return 'earth';
      case 'divine_aura': return 'aura';
      // `CarriedStore` is permanent sparse history: a boulder a god moved is
      // somewhere else for good, so the moving of it endured the moment it
      // happened, exactly as an anchor and an earthwork do
      case 'divine_grasp': return 'grasp';
      case 'fire_started': return 'fire';
      case 'fire_burned': return 'burn';
      // somebody walked to where a sign pointed; a life crossed a line the
      // card speaks at. Neither is ever divine itself, so neither reaches a
      // strand except as the causal child of the cast that led to it.
      case 'omen_answered': return 'answer';
      case 'person_changed': return 'becoming';
      default: return undefined;
    }
  }

  private extend(godId: string, verb: string, e: EventRecord): string {
    const named = typeof e.data?.targetId === 'string' ? e.data.targetId : undefined;
    const subject = named ?? `cell:${placeKey(e.x, e.z, CHRONICLE_CELL)}`;
    const key = `${godId}|${verb}|${subject}`;
    let s = this.strandMap.get(key);
    if (!s) {
      s = { key, godId, verb, subject, x: e.x, z: e.z,
            first: e.time, last: e.time, acts: 0, travel: 0, left: [] };
      this.strandMap.set(key, s);
    }
    s.acts += 1;
    s.last = Math.max(s.last, e.time);
    s.travel = Math.max(s.travel, Math.hypot(e.x - s.x, e.z - s.z));

    const tally = this.tally(godId);
    tally.acts += 1;
    tally.first = tally.first === undefined ? e.time : Math.min(tally.first, e.time);
    tally.last = tally.last === undefined ? e.time : Math.max(tally.last, e.time);
    return key;
  }

  private mark(e: EventRecord, divine: boolean, godId?: string): void {
    const key = `${e.type}|${placeKey(e.x, e.z, MARK_CELL)}`;
    const m = this.markMap.get(key);
    if (!m) {
      this.markMap.set(key, {
        key, type: e.type, x: e.x, z: e.z,
        first: e.time, last: e.time, count: 1, divine,
        ...(divine && godId ? { by: godId } : {}),
      });
      return;
    }
    m.count += 1;
    m.last = Math.max(m.last, e.time);
    if (divine) {
      m.divine = true;
      if (godId && m.by === undefined) m.by = godId;
    }
  }

  private endured(key: string, at: number, how: Endurance): void {
    const s = this.strandMap.get(key);
    if (!s) return;
    if (s.enduredAt === undefined) s.enduredAt = at;
    if (!s.left.includes(how)) s.left.push(how);
    // …and a thing the world confirms again is standing again. A tree pushed
    // back over the line is not a lapsed promotion; it is a promotion.
    delete s.endedAt;
  }

  /**
   * THE WORLD WITHDRAWS ITS CONFIRMATION — a promoted thing has decayed back
   * to ordinary, and whatever stood on that alone stops standing.
   *
   * Keyed on the PLACE and not on a god, because if the tree lapsed it lapsed
   * for everyone who had a hand in it. Idempotent, so the daily sweep
   * that calls it may call it as often as it likes.
   *
   * The gate is the honest part: a strand is only ended if `how` is the
   * **whole** of why it endured. `left` can hold several — a stone that was
   * promoted AND had earth moved at it AND was grasped has three, and the
   * promotion lapsing takes nothing away from the other two. Only a deed
   * resting on promotion alone falls with it.
   *
   * `how` defaults to `promotion`, the first endurance that could lapse; a
   * `becoming` lapses the same way when the person dies (`record`, step 0),
   * keyed on the person's bare id, which is what a cast on a person names.
   */
  lapsed(place: string, at: number, how: Endurance = 'promotion'): void {
    for (const s of this.strandMap.values()) {
      /**
       * MATCHED ON THE PLACE, NOT ON THE STRING, and this is the line the
       * whole feature turns on.
       *
       * `ThingStore` keys a mark on a bare `placeKey(x, z)` — `"-35634:21056"`
       * — while a strand's subject is the `targetId` the cast carried, which
       * is a `Target.id`: `"plant:-35634:21056"`. The first cut of this
       * compared them with `===` and would therefore have lapsed **nothing at
       * all**, for ever, while looking entirely wired. That is the exact
       * failure `WIRING.md` exists to catch, arriving inside the fix for one
       * of its own rows.
       *
       * `endsWith(':' + place)` is unambiguous because a place key is itself a
       * colon-joined coordinate pair, so the suffix cannot collide with a
       * shorter one; the bare form is accepted too, for a subject that never
       * had a kind prefix.
       */
      if (s.subject !== place && !s.subject.endsWith(`:${place}`)) continue;
      if (s.enduredAt === undefined || s.endedAt !== undefined) continue;
      if (s.left.length !== 1 || s.left[0] !== how) continue;
      // never before it endured: a lapse recorded early would read as a
      // negative lifetime and `standingOf` would take the age as zero
      s.endedAt = Math.max(s.enduredAt, at);
    }
  }

  private remember(id: string, key: string): void {
    this.pending.set(id, key);
    if (this.pending.size > PENDING_MAX) {
      // Map iterates in insertion order, so the first key is the oldest
      const oldest = this.pending.keys().next();
      if (!oldest.done) this.pending.delete(oldest.value);
    }
  }

  private tally(godId: string): Tally {
    let t = this.tallyMap.get(godId);
    if (!t) { t = { acts: 0, retired: 0, retiredActs: 0 }; this.tallyMap.set(godId, t); }
    return t;
  }

  /**
   * Forget what came to nothing. Law 2's other half: the journal grows with
   * what a god made durable, never with what it did. What is dropped is
   * *counted* rather than silently lost — a record that says "and eleven
   * thousand other acts left nothing behind" is a true one, and it is the
   * sentence law 25 wants a god to be able to read about itself.
   *
   * Called on the day grid, so this is a handful of keys against a sparse map.
   */
  sweep(now: number): number {
    let dropped = 0;
    for (const [key, s] of [...this.strandMap]) {
      if (s.enduredAt !== undefined) continue;
      if (now - s.last <= FORGET_AFTER) continue;
      const t = this.tally(s.godId);
      t.retired += 1;
      t.retiredActs += s.acts;
      this.strandMap.delete(key);
      dropped += 1;
    }
    return dropped;
  }

  /* ── reading ─────────────────────────────────────────────────────────── */

  get size(): number { return this.strandMap.size; }
  get markCount(): number { return this.markMap.size; }

  all(): readonly Strand[] { return [...this.strandMap.values()]; }
  marks(): readonly Mark[] { return [...this.markMap.values()]; }

  strandsOf(godId: string): Strand[] {
    return [...this.strandMap.values()].filter((s) => s.godId === godId);
  }

  /** Strands the world has confirmed. These are the ones history is made of. */
  enduringOf(godId: string): Strand[] {
    return this.strandsOf(godId).filter((s) => s.enduredAt !== undefined);
  }

  tallyOf(godId: string): Readonly<Tally> {
    return this.tallyMap.get(godId) ?? { acts: 0, retired: 0, retiredActs: 0 };
  }

  /**
   * How far apart this god's enduring deeds are, in metres — the diagonal of
   * their bounding box. A bounding box rather than the true maximum pairwise
   * distance because it is O(n) instead of O(n²) and never *under*-states the
   * separation, so a qualification cut on it can only ever be conservative in
   * the direction that matters.
   */
  spanOf(godId: string): number {
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    let any = false;
    for (const s of this.strandMap.values()) {
      if (s.godId !== godId || s.enduredAt === undefined) continue;
      any = true;
      minX = Math.min(minX, s.x); maxX = Math.max(maxX, s.x);
      minZ = Math.min(minZ, s.z); maxZ = Math.max(maxZ, s.z);
    }
    return any ? Math.hypot(maxX - minX, maxZ - minZ) : 0;
  }

  /** Durable changes recorded within `radius` of a point, at or after `since`. */
  marksNear(x: number, z: number, radius: number, since = -Infinity): Mark[] {
    return [...this.markMap.values()].filter((m) =>
      m.last >= since && Math.hypot(m.x - x, m.z - z) <= radius);
  }

  /* ── persistence ─────────────────────────────────────────────────────── */

  toSnapshot(): ChronicleSnapshot {
    return {
      strands: [...this.strandMap.values()].map((s) => ({ ...s, left: [...s.left] })),
      marks: [...this.markMap.values()].map((m) => ({ ...m })),
      tallies: [...this.tallyMap].map(([godId, t]) => ({ godId, ...t })),
      pending: [...this.pending].map(([id, key]) => ({ id, key })),
    };
  }

  /** Replace in place. Shares nothing with the source; insertion order kept. */
  restore(s: ChronicleSnapshot): void {
    this.strandMap.clear();
    this.markMap.clear();
    this.tallyMap.clear();
    this.pending.clear();
    for (const st of s.strands ?? []) this.strandMap.set(st.key, { ...st, left: [...st.left] });
    for (const m of s.marks ?? []) this.markMap.set(m.key, { ...m });
    for (const { godId, ...t } of s.tallies ?? []) this.tallyMap.set(godId, { ...t });
    for (const p of s.pending ?? []) this.pending.set(p.id, p.key);
  }

  static fromSnapshot(s: ChronicleSnapshot | undefined): Chronicle {
    const c = new Chronicle();
    if (s) c.restore(s);
    return c;
  }
}

/** Re-exported so a reader does not have to know which file `EventCause` is in. */
export type { EventCause };
